import { Form, Modal, message, Select } from 'antd';
import React, { useEffect, useState } from 'react';
import {
  fetchContactOptions,
  fetchOpportunityOptions,
  type RelationOption,
} from '@/modules/zy-crm/utils/relation-options';
import { createContactOpportunity } from '@/services/bff/contact-opportunity.api';

export type ContactOpportunityCreateContext = {
  customerUuid?: string;
  customerName?: string;
  contactUuid?: string;
  contactName?: string;
  opportunityUuid?: string;
  opportunityName?: string;
};

type ContactOpportunityCreateModalProps = {
  open: boolean;
  context?: ContactOpportunityCreateContext;
  onCancel: () => void;
  onSuccess: (record: { contactUuid: string; opportunityUuid: string }) => void;
};

const normalizeText = (value: unknown) =>
  typeof value === 'string' ? value.trim() : '';

export function ContactOpportunityCreateModal({
  open,
  context,
  onCancel,
  onSuccess,
}: ContactOpportunityCreateModalProps) {
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);
  const [contactOptions, setContactOptions] = useState<RelationOption[]>([]);
  const [opportunityOptions, setOpportunityOptions] = useState<
    RelationOption[]
  >([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) {
      return;
    }
    const nextContext = context || {};
    form.setFieldsValue({
      contactUuid: nextContext.contactUuid,
      opportunityUuid: nextContext.opportunityUuid,
    });
    setLoading(true);
    void Promise.all([
      fetchContactOptions(undefined, nextContext.customerUuid),
      fetchOpportunityOptions(),
    ])
      .then(([contacts, opportunities]) => {
        const ensuredContacts = [...contacts];
        const ensuredOpportunities = [...opportunities];
        if (
          normalizeText(nextContext.contactUuid) &&
          !ensuredContacts.some(
            (item) => item.value === normalizeText(nextContext.contactUuid),
          )
        ) {
          ensuredContacts.unshift({
            value: normalizeText(nextContext.contactUuid),
            label:
              normalizeText(nextContext.contactName) ||
              normalizeText(nextContext.contactUuid),
          });
        }
        if (
          normalizeText(nextContext.opportunityUuid) &&
          !ensuredOpportunities.some(
            (item) => item.value === normalizeText(nextContext.opportunityUuid),
          )
        ) {
          ensuredOpportunities.unshift({
            value: normalizeText(nextContext.opportunityUuid),
            label:
              normalizeText(nextContext.opportunityName) ||
              normalizeText(nextContext.opportunityUuid),
          });
        }
        setContactOptions(ensuredContacts);
        setOpportunityOptions(ensuredOpportunities);
      })
      .catch((error) => {
        message.error(error?.message || '关联选项加载失败');
      })
      .finally(() => {
        setLoading(false);
      });
  }, [context, form, open]);

  const submit = async () => {
    const values = await form.validateFields();
    setSubmitting(true);
    try {
      const record = await createContactOpportunity({
        contactUuid: String(values.contactUuid || ''),
        opportunityUuid: String(values.opportunityUuid || ''),
      });
      message.success('关联成功');
      onSuccess({
        contactUuid: String(record?.contactUuid || values.contactUuid || ''),
        opportunityUuid: String(
          record?.opportunityUuid || values.opportunityUuid || '',
        ),
      });
      form.resetFields();
    } catch (error: any) {
      message.error(error?.message || '关联失败');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      closable={{ 'aria-label': '关闭' }}
      keyboard
      open={open}
      title="关联联系人商机"
      onCancel={() => {
        form.resetFields();
        onCancel();
      }}
      onOk={() => void submit()}
      confirmLoading={submitting}
      destroyOnHidden
      forceRender
    >
      <Form form={form} layout="vertical" preserve={false}>
        <Form.Item
          label="联系人"
          name="contactUuid"
          rules={[{ required: true, message: '请选择联系人' }]}
        >
          <Select
            showSearch
            optionFilterProp="label"
            options={contactOptions}
            loading={loading}
            disabled={Boolean(normalizeText(context?.contactUuid))}
            placeholder="请选择联系人"
          />
        </Form.Item>
        <Form.Item
          label="商机"
          name="opportunityUuid"
          rules={[{ required: true, message: '请选择商机' }]}
        >
          <Select
            showSearch
            optionFilterProp="label"
            options={opportunityOptions}
            loading={loading}
            disabled={Boolean(normalizeText(context?.opportunityUuid))}
            placeholder="请选择商机"
          />
        </Form.Item>
      </Form>
    </Modal>
  );
}
